import { ButtonToggleGroup } from 'components/ButtonToggleGroup';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextField } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { SignatureViewer } from 'components/Signature/SignatureViewer';
import { useConfirm, useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useSelector } from 'hooks/useSelector';
import { UI_DATE_FORMAT_LONG, UI_TIME_FORMAT } from 'lib/constants';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import {
  dateToNumber,
  formatDate,
  numberToDate,
  randomKey,
} from 'lib/riskbase';
import { smartText } from 'lib/str';
import { theme } from 'lib/theme';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL_BACK,
  doAseDeleteSignatory,
  doAseSaveSignatory,
  doShowSignatureDialog,
  eventTypeSignatureTaken,
} from 'store/actions';
import {
  selectAseIsOnline,
  selectAseSignatoryResult,
  selectAseStatus,
  selectAseTplSignatory,
} from 'store/assessments/selectors';
import { AssessmentStatusesEnum } from 'types/types';

export const SignatoryModal: React.FC<
  ModalScreenProps<ModalScreens.AseSignatory>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();

  const { signatoryId } = route.params;
  const eventKeySignature = React.useRef<string>(randomKey());

  const isOnline = useSelector(selectAseIsOnline);
  const aseStatus = useSelector(selectAseStatus).id as AssessmentStatusesEnum;
  const signatory = useSelector((state) =>
    selectAseTplSignatory(state, signatoryId),
  );
  const signatoryResult = useSelector((state) =>
    selectAseSignatoryResult(state, signatoryId),
  );
  const canEdit = aseStatus === AssessmentStatusesEnum.inprogress && !isOnline;
  const isNew = !signatoryResult;

  const initValues = React.useRef({
    name: signatoryResult?.name || '',
    signature: signatoryResult?.signature || '',
    unavailable: !!signatoryResult?.unavailable,
    timestamp: signatoryResult?.timestamp || 0,
  });
  const changes = React.useRef(cloneDeep(initValues.current));
  const checkIsDirty = React.useCallback(
    () => !isEqual(initValues.current, changes.current),
    [],
  );
  const isDirty = React.useRef<boolean>(false);
  isDirty.current = checkIsDirty();

  const handleChangeWithName = React.useCallback(
    <
      N extends keyof typeof changes.current,
      V extends (typeof changes.current)[N],
    >(
      name: N,
      value: V,
    ) => {
      changes.current[name] = value;
      if (isDirty.current !== checkIsDirty()) {
        forceUpdate();
      }
    },
    [checkIsDirty, forceUpdate],
  );

  const handleChangeUnavailable = React.useCallback(
    (newValue: boolean) => {
      changes.current.unavailable = newValue;
      changes.current.timestamp =
        (!newValue ||
          changes.current.unavailable === initValues.current.unavailable) &&
        changes.current.signature === initValues.current.signature
          ? initValues.current.timestamp
          : dateToNumber(new Date());
      forceUpdate();
    },
    [forceUpdate],
  );

  const handleSave = React.useCallback(() => {
    dispatch(
      doAseSaveSignatory({
        signatoryId,
        name: changes.current.name,
        signature: changes.current.signature,
        unavailable: changes.current.unavailable,
        timestamp: changes.current.timestamp,
      }),
    );
  }, [dispatch, signatoryId]);

  const handleDelete = React.useCallback(() => {
    confirm({
      text: changes.current.unavailable
        ? smartText('The signatory will be deleted')
        : smartText("The signatory's name and signature will be deleted"),
      title: 'Delete Signatory',
      confirmText: 'Delete',
      destructive: true,
      onConfirm: () =>
        dispatch(
          doAseDeleteSignatory({
            signatoryId,
          }),
        ),
    });
  }, [confirm, dispatch, signatoryId]);

  const handleClose = React.useCallback(() => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, handleSave, dispatch]);

  const handleOpenSignature = () =>
    dispatch(
      doShowSignatureDialog({
        eventKey: eventKeySignature.current,
      }),
    );

  React.useEffect(() => {
    const events = [
      doAddEvent(eventTypeSignatureTaken, async (event) => {
        if (event.payload.eventKey === eventKeySignature.current) {
          changes.current.signature = event.payload.signature;
          changes.current.timestamp = dateToNumber(new Date());
          forceUpdate();
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [forceUpdate]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  if (!signatory) {
    return null;
  }

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Signatory"
          cancelButtonText="Close"
          saveButtonText="Save"
          onPressCancelButton={handleClose}
          onPressSaveButton={canEdit ? handleSave : undefined}
          menuRight={
            !isNew && canEdit
              ? {
                  iconButtonConfig: {
                    icon: ICONS.EllipsisLight,
                  },
                  options: [
                    {
                      text: 'Delete Signatory',
                      onPress: handleDelete,
                      destructive: true,
                      icon: ICONS.TrashCanLight,
                    },
                  ],
                }
              : undefined
          }
        />
      }
    >
      <Card
        marginBottomHalf={!signatory.requireOnSubmit}
        explanation={
          signatory.requireOnSubmit
            ? 'Signature required before assessment can be submitted'
            : undefined
        }
      >
        <CardRow
          label={signatory.name}
          value={signatory.declaration}
          valueColor={theme.colors.textBlack64}
          labelNumberOfLines={0}
          labelAlignTop
        />
      </Card>
      {canEdit ? (
        <>
          {(signatory.allowUnavailable || changes.current.unavailable) && (
            <ButtonToggleGroup
              buttons={[
                { text: 'Person is available', key: 'available' },
                { text: 'Person is unavailable', key: 'unavailable' },
              ]}
              onActiveChange={(newActive) =>
                handleChangeUnavailable(newActive === 'unavailable')
              }
              active={changes.current.unavailable ? 'unavailable' : 'available'}
              containerStyle={{
                marginBottom: 12,
                alignSelf: 'stretch',
                marginHorizontal: theme.paddingHorizontal,
              }}
              rounded
              size="small"
            />
          )}
          {!changes.current.unavailable ? (
            <Card marginBottomHalf>
              <TextField
                name="name"
                value={changes.current.name}
                placeholder={smartText(`Name of ${signatory.name}`)}
                onChangeWithName={handleChangeWithName}
                onEsc={handleClose}
                autoFocusWeb
                autoFocusDelay
              />
              {!!changes.current.signature && (
                <SignatureViewer
                  key={changes.current.signature}
                  signature={changes.current.signature}
                />
              )}
              <CardRow
                label={
                  !changes.current.signature
                    ? 'Capture Signature'
                    : 'Update Signature'
                }
                labelColor={theme.colors.blue}
                icon={ICONS.SignatureLight}
                iconColor={theme.colors.blue}
                onPress={handleOpenSignature}
              />
            </Card>
          ) : (
            <Card marginBottomHalf>
              <CardRow placeholder="No Name Collected" />
              <CardRow placeholder="No Signature Collected" />
            </Card>
          )}
          {!!changes.current.timestamp && (
            <Card explanation="Date and time are updated when the signature is updated">
              <CardRow
                value={`${formatDate(
                  numberToDate(changes.current.timestamp),
                  UI_DATE_FORMAT_LONG,
                )} ${formatDate(
                  numberToDate(changes.current.timestamp),
                  UI_TIME_FORMAT,
                )}`}
              />
            </Card>
          )}
        </>
      ) : (
        <Card>
          {changes.current.unavailable && (
            <CardRow
              label="Signatory Was Unavailable"
              icon={ICONS.CircleMinusSolid}
            />
          )}
          <CardRow
            value={changes.current.name}
            placeholder="No Name Collected"
          />
          {changes.current.signature ? (
            <SignatureViewer
              key={changes.current.signature}
              signature={changes.current.signature}
            />
          ) : (
            <CardRow placeholder="No Signature Collected" />
          )}
          {signatoryResult && (
            <CardRow
              value={`${formatDate(
                numberToDate(signatoryResult.timestamp),
                UI_DATE_FORMAT_LONG,
              )} ${formatDate(
                numberToDate(signatoryResult.timestamp),
                UI_TIME_FORMAT,
              )}`}
            />
          )}
        </Card>
      )}
    </PageScrollView>
  );
};
